/**
 * Parse one tag-rule condition from bulk-import JSON.
 */

import type { TagRuleCondition } from '@/hooks';

const MAX_COUNTRY_THRESHOLDS = 50;

export function parseBulkImportCondition(
  cond: Record<string, unknown>,
  ruleIdx: number,
  condIdx: number,
  allowedFields: Set<string>,
  allowedOps: Set<string>,
): { ok: true; condition: TagRuleCondition } | { ok: false; error: string } {
  const prefix = `Rule ${ruleIdx + 1}, condition ${condIdx + 1}`;
  const field = cond.field;
  const operator = cond.operator;
  let value = cond.value;
  if (typeof field !== 'string' || !allowedFields.has(field)) {
    return { ok: false, error: `${prefix}: invalid field` };
  }
  if (typeof operator !== 'string' || !allowedOps.has(operator)) {
    return { ok: false, error: `${prefix}: invalid operator` };
  }
  if (typeof value === 'string') value = Number(value);
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { ok: false, error: `${prefix}: value must be a number` };
  }
  const base: TagRuleCondition = {
    field: field as TagRuleCondition['field'],
    operator: operator as TagRuleCondition['operator'],
    value,
  };
  const rawCt = cond.countryThresholds;
  if (rawCt === undefined) return parseCampaignThresholds(cond, prefix, base);
  if (!Array.isArray(rawCt)) {
    return { ok: false, error: `${prefix}: countryThresholds must be an array when present` };
  }
  if (rawCt.length > MAX_COUNTRY_THRESHOLDS) {
    return {
      ok: false,
      error: `${prefix}: at most ${MAX_COUNTRY_THRESHOLDS} countryThresholds entries`,
    };
  }
  const seen = new Set<string>();
  const countryThresholds: NonNullable<TagRuleCondition['countryThresholds']> = [];
  for (let k = 0; k < rawCt.length; k++) {
    const row = rawCt[k];
    if (!row || typeof row !== 'object') {
      return { ok: false, error: `${prefix}, countryThresholds[${k}]: invalid entry` };
    }
    const rec = row as Record<string, unknown>;
    const ck =
      typeof rec.countryKey === 'string' ? rec.countryKey.trim().toLowerCase() : '';
    let v = rec.value;
    if (typeof v === 'string') v = Number(v);
    if (!ck) {
      return { ok: false, error: `${prefix}, countryThresholds[${k}]: countryKey required` };
    }
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      return { ok: false, error: `${prefix}, countryThresholds[${k}]: value must be a number` };
    }
    if (seen.has(ck)) {
      return { ok: false, error: `${prefix}: duplicate countryKey in countryThresholds: ${ck}` };
    }
    seen.add(ck);
    countryThresholds.push({ countryKey: ck, value: v });
  }
  const withCountries =
    countryThresholds.length > 0 ? { ...base, countryThresholds } : base;
  return parseCampaignThresholds(cond, prefix, withCountries);
}

const PROVIDERS = new Set(['VOLUUM', 'BINOM']);

/** campaignThresholds beat countryThresholds, so they are validated just as strictly. */
function parseCampaignThresholds(
  cond: Record<string, unknown>,
  prefix: string,
  base: TagRuleCondition,
): { ok: true; condition: TagRuleCondition } | { ok: false; error: string } {
  const raw = cond.campaignThresholds;
  if (raw === undefined) return { ok: true, condition: base };
  if (!Array.isArray(raw)) {
    return { ok: false, error: `${prefix}: campaignThresholds must be an array when present` };
  }
  if (raw.length > MAX_COUNTRY_THRESHOLDS) {
    return {
      ok: false,
      error: `${prefix}: at most ${MAX_COUNTRY_THRESHOLDS} campaignThresholds entries`,
    };
  }
  const seen = new Set<string>();
  const campaignThresholds: NonNullable<TagRuleCondition['campaignThresholds']> = [];
  for (let k = 0; k < raw.length; k++) {
    const row = raw[k];
    if (!row || typeof row !== 'object') {
      return { ok: false, error: `${prefix}, campaignThresholds[${k}]: invalid entry` };
    }
    const rec = row as Record<string, unknown>;
    const provider = typeof rec.provider === 'string' ? rec.provider.trim().toUpperCase() : '';
    const campaignId = typeof rec.campaignId === 'string' ? rec.campaignId.trim() : '';
    let v = rec.value;
    if (typeof v === 'string') v = Number(v);
    if (!PROVIDERS.has(provider)) {
      return {
        ok: false,
        error: `${prefix}, campaignThresholds[${k}]: provider must be VOLUUM or BINOM`,
      };
    }
    if (!campaignId) {
      return { ok: false, error: `${prefix}, campaignThresholds[${k}]: campaignId required` };
    }
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      return { ok: false, error: `${prefix}, campaignThresholds[${k}]: value must be a number` };
    }
    // Keyed by the pair — Voluum "1" and Binom "1" are different campaigns.
    const key = `${provider}:${campaignId.toLowerCase()}`;
    if (seen.has(key)) {
      return {
        ok: false,
        error: `${prefix}: duplicate campaign in campaignThresholds: ${provider} ${campaignId}`,
      };
    }
    seen.add(key);
    campaignThresholds.push({
      provider: provider as 'VOLUUM' | 'BINOM',
      campaignId,
      value: v,
    });
  }
  if (campaignThresholds.length === 0) return { ok: true, condition: base };
  return { ok: true, condition: { ...base, campaignThresholds } };
}
